import React, { useState } from 'react';
import { IconClipboardList } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';

import { cn } from '@/lib/utils';
import { Loader, LoaderModeType } from './Loader';
import { TotemCheckbox } from './TotemCheckbox';

export type AcceptedFieldElement = JSX.Element | string | number | boolean | JSX.Element[];

export type SortFunction<T> = (itemA: FormattedField<T>, itemB: FormattedField<T>) => number;

export type FieldToDisplay<T> = {
    fieldName: keyof T;
    label: string;
    sortFunction?: SortFunction<T>;
    maxWidth?: string;
    minWidth?: string;
    sublabel?: string;
};

export type ListViewProps<T> = {
    fieldsToDisplay: FieldToDisplay<T>[];
    isSmallDisplay?: boolean;
    items: FormattedField<T>[];
    linkBasePath?: string;
    linkSuffixPath?: string;
    keyExtractor?: (item: FormattedField<T>) => AcceptedFieldElement;
    loadMore?: () => void;
    hasMore?: boolean;
    onItemClick?: (item: FormattedField<T>) => void;
    highlightedKeys?: string[];
    selectedKeys?: string[];
    setSelectedKeys?: (value: string[]) => void;
    onSort?: (fieldName: keyof T, sortOrder: 'asc' | 'desc' | '') => void;
    currentSort?: { fieldName: keyof T; sortOrder: 'asc' | 'desc' | '' };
};

type FormattedField<T> = Record<keyof T, AcceptedFieldElement>;

export function ListView<T>({
    fieldsToDisplay,
    isSmallDisplay,
    items,
    linkBasePath,
    linkSuffixPath,
    keyExtractor,
    loadMore,
    hasMore,
    onItemClick,
    highlightedKeys,
    selectedKeys,
    setSelectedKeys,
    onSort,
    currentSort,
}: ListViewProps<T>) {
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [selectedSort, setSelectedSort] = useState<SortFunction<T> | null>(null);
    const [isSortReverse, setIsSortReverse] = useState<boolean>(false);

    linkSuffixPath = linkSuffixPath || '';

    const keyExtractorClosure = (index: number, item: FormattedField<T>, areKeysUnique: boolean) => {
        const key = keyExtractor && areKeysUnique ? keyExtractor(item) : index.toString();
        if (typeof key === 'string') return key;
        return index.toString();
    };

    const handleScroll = async (e: React.UIEvent<HTMLElement>) => {
        const element = e.currentTarget;
        const scrollThreshold = 0.8;
        if (
            !isFetchingMore &&
            hasMore &&
            loadMore &&
            element.scrollTop > scrollThreshold * (element.scrollHeight - element.clientHeight)
        ) {
            setIsFetchingMore(true);
            await loadMore();
            setIsFetchingMore(false);
        }
    };

    function toggleSelection(uniqueKey: string) {
        if (!selectedKeys || !setSelectedKeys) {
            return;
        }
        if (selectedKeys.includes(uniqueKey)) {
            setSelectedKeys(selectedKeys.filter((selectedKey) => selectedKey !== uniqueKey));
        } else {
            setSelectedKeys([...selectedKeys, uniqueKey]);
        }
    }

    // if we have the same key twice, the table does weird stuff. This is a check to prevent them.
    const areKeysUnique = items.every(
        (value, valueIndex, array) =>
            array.findIndex(
                (otherValue, otherValueIndex) =>
                    keyExtractorClosure(valueIndex, value, true) ===
                    keyExtractorClosure(otherValueIndex, otherValue, true),
            ) === valueIndex,
    );

    const sortedItemsAsc = selectedSort ? [...items].sort(selectedSort) : [...items];
    const sortedItems = isSortReverse ? sortedItemsAsc.reverse() : sortedItemsAsc;

    return (
        <div className="w-full h-full overflow-y-auto z-0" onScroll={handleScroll}>
            {!areKeysUnique ? (
                <div className="text-destructive">
                    La liste contient deux items similaires, cela n'est pas normal, contactez un dev
                </div>
            ) : null}
            <table className="w-full text-[17px] border-spacing-0">
                <thead className="relative">
                    <tr>
                        {selectedKeys && setSelectedKeys ? (
                            <th className="sticky top-0 z-10 bg-background text-foreground text-left p-2.5 border-b-2 border-border" />
                        ) : null}
                        {fieldsToDisplay.map(
                            ({ label, sortFunction, maxWidth, minWidth, sublabel, fieldName }, index) => (
                                <th
                                    key={index}
                                    className={cn(
                                        'sticky top-0 z-10 bg-background text-foreground text-left border-b-2 border-border',
                                        isSmallDisplay
                                            ? 'text-[13px] p-1 whitespace-nowrap overflow-hidden text-ellipsis'
                                            : 'p-2.5',
                                    )}
                                    style={{
                                        maxWidth: maxWidth ?? undefined,
                                        minWidth: minWidth ?? undefined,
                                    }}
                                    title={label}
                                >
                                    {sortFunction ? (
                                        <button
                                            type="button"
                                            className={cn(
                                                'cursor-pointer text-center p-0 m-0 bg-transparent border-none outline-none',
                                                (
                                                    onSort
                                                        ? currentSort?.fieldName === fieldName &&
                                                          currentSort?.sortOrder !== ''
                                                        : selectedSort === sortFunction
                                                )
                                                    ? 'text-primary'
                                                    : 'text-foreground',
                                            )}
                                            onClick={() => {
                                                if (onSort) {
                                                    let newOrder: 'asc' | 'desc' | '' = 'asc';
                                                    if (currentSort?.fieldName === fieldName) {
                                                        if (currentSort.sortOrder === 'asc') newOrder = 'desc';
                                                        else if (currentSort.sortOrder === 'desc') newOrder = '';
                                                    }
                                                    onSort(fieldName, newOrder);
                                                } else {
                                                    if (selectedSort === sortFunction) {
                                                        if (isSortReverse) {
                                                            setSelectedSort(null);
                                                            setIsSortReverse(false);
                                                        } else {
                                                            setIsSortReverse(true);
                                                        }
                                                    } else {
                                                        setSelectedSort(() => sortFunction);
                                                        setIsSortReverse(false);
                                                    }
                                                }
                                            }}
                                        >
                                            {onSort ? (
                                                currentSort?.fieldName !== fieldName || !currentSort?.sortOrder ? (
                                                    <FaSort />
                                                ) : currentSort.sortOrder === 'desc' ? (
                                                    <FaSortDown />
                                                ) : (
                                                    <FaSortUp />
                                                )
                                            ) : selectedSort !== sortFunction ? (
                                                <FaSort />
                                            ) : isSortReverse ? (
                                                <FaSortDown />
                                            ) : (
                                                <FaSortUp />
                                            )}
                                        </button>
                                    ) : null}
                                    {label}
                                    {sublabel ? (
                                        <div
                                            className={cn(
                                                'absolute bottom-[1px] text-[9px] text-muted-foreground',
                                                isSmallDisplay ? 'left-1' : 'left-2.5',
                                            )}
                                        >
                                            {sublabel}
                                        </div>
                                    ) : null}
                                </th>
                            ),
                        )}
                        {linkBasePath ? (
                            <th className="sticky top-0 z-10 bg-background text-foreground text-left p-2.5 border-b-2 border-border">
                                <b>Détails</b>
                            </th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    {sortedItems.map((item, index) => {
                        const uniqueKey = keyExtractorClosure(index, item, areKeysUnique);
                        const detailsLink = `${linkBasePath}${uniqueKey}${linkSuffixPath}`;
                        const isSelected = selectedKeys?.includes(uniqueKey) ?? false;
                        const isHighlighted = !!highlightedKeys?.includes(uniqueKey);
                        return (
                            <tr
                                key={uniqueKey}
                                className={cn(
                                    !!onItemClick && 'cursor-pointer',
                                    isHighlighted ? 'text-primary' : 'text-foreground',
                                )}
                                onClick={() => {
                                    if (onItemClick) {
                                        onItemClick(item);
                                    }
                                }}
                            >
                                {selectedKeys && setSelectedKeys ? (
                                    <td
                                        className={cn(
                                            'whitespace-pre-wrap border-b border-border',
                                            isSmallDisplay ? 'p-1 text-[15px]' : 'p-2.5',
                                        )}
                                    >
                                        <TotemCheckbox
                                            checked={isSelected}
                                            onChange={() => toggleSelection(uniqueKey)}
                                        />
                                    </td>
                                ) : null}
                                {fieldsToDisplay.map(({ fieldName, maxWidth, minWidth }, index) => (
                                    <td
                                        key={index}
                                        className={cn(
                                            'whitespace-pre-wrap border-b border-border',
                                            isSmallDisplay ? 'p-1 text-[15px]' : 'p-2.5',
                                        )}
                                        style={{
                                            maxWidth: maxWidth ?? undefined,
                                            minWidth: minWidth ?? undefined,
                                        }}
                                        data-test={fieldName}
                                    >
                                        {item[fieldName]}
                                    </td>
                                ))}
                                {linkBasePath ? (
                                    <td
                                        className={cn(
                                            'whitespace-pre-wrap border-b border-border',
                                            isSmallDisplay ? 'p-1 text-[15px]' : 'p-2.5',
                                        )}
                                    >
                                        <Link to={detailsLink} data-test="more-details">
                                            <div className="flex items-center">
                                                <IconClipboardList size={22} className="text-foreground" />
                                            </div>
                                        </Link>
                                    </td>
                                ) : null}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {isFetchingMore ? (
                <div>
                    <Loader mode={LoaderModeType.Spin} />
                </div>
            ) : null}
        </div>
    );
}
